import { CommonLinks, getCommonLinks } from '@lib/features/commonLinks';
import { AppIndexData } from '@lib/features/entryIndexes';
import { AppGlobals, getGlobals } from '@lib/features/globals/getGlobals';
import { AppNavigationMenus, getAppNavigationMenus } from '@lib/features/navigation/navigation';
import { View } from '@lib/fetch/getViewByUri';
import { createContext } from 'react';

export type AppContext = {
  nav?: AppNavigationMenus;
  indexData?: AppIndexData | null;
  globals?: AppGlobals;
  commonLinks?: CommonLinks;
  view?: View | null;
};

const initialState: AppContext = {};

// Matches the default `revalidate = 30` in getStaticProps.ts — caching this any longer would
// serve data staler than what ISR already tolerates for every page.
const CONTEXT_CACHE_TTL_MS = 30_000;

const ttlMemoize = <T>(fn: () => Promise<T>): (() => Promise<T>) => {
  let cached: Promise<T> | null = null;
  let expiresAt = 0;

  return () => {
    if (!cached || Date.now() > expiresAt) {
      expiresAt = Date.now() + CONTEXT_CACHE_TTL_MS;
      cached = fn().catch((err) => {
        cached = null;
        throw err;
      });
    }
    return cached;
  };
};

const getCachedAppNavigationMenus = ttlMemoize(getAppNavigationMenus);
const getCachedGlobals = ttlMemoize(getGlobals);
const getCachedCommonLinks = ttlMemoize(getCommonLinks);

export const getAppContext = async (defaults?: Partial<AppContext>): Promise<AppContext> => {
  const [nav, globals, commonLinks] = await Promise.all([
    getCachedAppNavigationMenus(),
    getCachedGlobals(),
    getCachedCommonLinks(),
  ]);

  return {
    ...initialState,
    ...defaults,
    nav,
    commonLinks,
    globals,
  };
};

export const appContext = createContext<AppContext>(initialState);

export const { Provider: AppContextProvider } = appContext;
